<template>
  <div v-if="!loading" class="product">
    <PointsBanner />
    <div class="product-grid">
      <div class="product__media">
        <ProductMediaGallery />
      </div>
      <div ref="product-details" class="product__info">
        <div class="product__info-inner">
          <div class="product__title-wrapper">
            <h1 class="product__title">{{ product.title.toUpperCase() }}</h1>
            <div class="product__price">
              <ProductPrice show-percent />
            </div>
          </div>
          <Variants v-if="showVariants" class="variants-component" />
          <p v-if="showSellingPlanDescription" class="sellingPlanDescription">
            {{ sellingPlanDescription }}
          </p>
          <div class="grid">
            <div class="main">
              <div ref="addToBag" class="product__payment">
                <StockNotificationForm v-if="compactStockNotifOpen" />
                <AddToBag v-if="!compactStockNotifOpen" />
                <WishlistButton :product="product" :variant="variant" />
              </div>
              <ProductDetails show-read-more />
              <div class="product__bnpl">
                <BNPLInformation :show-icon="false" />
              </div>
              <div class="product__accordion">
                <AccordionGroup is-closed-by-default>
                  <Accordion key="styling" title="Styling">
                    <Styling />
                  </Accordion>
                  <Accordion
                    v-if="product?.fabricAndPrintDescription"
                    key="fabric"
                    title="Fabric"
                    open-by-default
                  >
                    <FabricAndPrint />
                  </Accordion>
                  <Accordion key="aftercare" title="Composition & Care">
                    <Aftercare />
                  </Accordion>
                  <Accordion key="sustainability" title="Sustainability">
                    <Sustainability />
                  </Accordion>
                  <Accordion key="shipping" title="Shipping & Returns">
                    <ShippingAndReturns />
                  </Accordion>
                </AccordionGroup>
              </div>
              <div class="purchase-info">
                <IconText
                  v-if="showDeliveryEstimateComponent"
                  icon="exclaimation-circle"
                  class="enhanced-edd"
                  use-background
                >
                  <DeliveryEstimatorNoInput
                    class="estimate-delivery-date"
                    show-countdown
                  />
                </IconText>
                <IconText
                  v-if="showPointsBanner"
                  icon="basket-plus"
                  use-background
                >
                  <p>
                    You have {{ creditBalance }} ({{ pointsBalance }} points) to
                    use at Checkout.
                  </p>
                </IconText>
                <IconText icon="hanger" use-background>
                  <Easysize
                    text="Not sure about your size?"
                    underline-text="Try our Fit Quiz."
                  />
                </IconText>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <HowToStyleIt />

    <CompleteTheSet />

    <HowToWear />

    <NostoProductRecommendations
      v-if="nostoProductRecommendationsProduct?.length > 0"
      :recommendations="nostoProductRecommendationsProduct"
      :placement="nostoProductRecommendationsProductId"
      heading="You might also like"
    />
    <ProductRecommendations v-else />

    <NostoProductRecommendations
      v-if="nostoProductRecommendationsBehaviour?.length > 0"
      :recommendations="nostoProductRecommendationsBehaviour"
      :placement="nostoProductRecommendationsBehaviourId"
      heading="An edit just for you"
    />
    <SizeGuide />

    <Transition name="fade">
      <StickyAddToBag v-if="showStickyAddToBag" />
    </Transition>
  </div>

  <ProductSkeleton v-else />
</template>

<script>
// Import components
import HowToWear from "@/components/product-display/HowToWearV2.vue";
import Variants from "@/components/product-display/VariantsV2.vue";
import AddToBag from "@/components/product-display/AddToBagV3.vue";
import IconText from "@/components/IconTextV2.vue";
import DeliveryEstimatorNoInput from "@/components/product-display/DeliveryEstimatorNoInputV3.vue";
import SizeGuide from "@/components/product-display/SizeGuideV2.vue";
import BNPLInformation from "@/components/product-display/BNPLInformationV2.vue";
import Easysize from "@/components/product-display/Easysize.vue";
import ProductRecommendations from "@/components/product-display/ProductRecommendationsV2.vue";
import ProductMediaGallery from "@/components/product-display/ProductMediaGalleryV2.vue";
import AccordionGroup from "@/components/AccordionGroup.vue";
import Accordion from "@/components/AccordionV2.vue";
import ProductDetails from "@/components/product-display/ProductDetails.vue";
import Sustainability from "@/components/product-display/Sustainability.vue";
import WishlistButton from "@/components/WishlistButtonV3.vue";
import ProductPrice from "@/components/product-display/ProductPrice.vue";
import Styling from "@/components/product-display/StylingV2.vue";
import ShippingAndReturns from "@/components/product-display/ShippingAndReturnsV2.vue";
import ProductSkeleton from "@/components/skeleton/ProductSkeletonV2.vue";
import Aftercare from "@/components/product-display/AftercareV2.vue";
import StickyAddToBag from "@/components/product-display/StickyAddToBagV2.vue";
import FabricAndPrint from "@/components/product-display/FabricAndPrint.vue";
import NostoProductRecommendations from "@/components/NostoProductRecommendationsV2.vue";
import CompleteTheSet from "@/components/product-display/CompleteTheSetV2.vue";
import StockNotificationForm from "@/components/StockNotificationCompact.vue";
import HowToStyleIt from "@/components/product-display/HowToStyleIt.vue";
import PointsBanner from "@/components/PointsBanner.vue";

// Import internal modules
import productQuery from "@/graphql/queries/ProductV2.gql";
import { regionFeatureToggles } from "@/config.js";
import { getParams } from "@/helpers/url.js";
import { viewProductEvent } from "@/helpers/events.js";
import { businessLogic } from "@/config.js";

// Import third parties
import { mapState, mapGetters, mapActions } from "vuex";

export default {
  name: "ProductPageV3",
  components: {
    HowToWear,
    Variants,
    AddToBag,
    IconText,
    DeliveryEstimatorNoInput,
    SizeGuide,
    BNPLInformation,
    Easysize,
    ProductRecommendations,
    ProductMediaGallery,
    AccordionGroup,
    Accordion,
    ProductDetails,
    WishlistButton,
    ProductPrice,
    Sustainability,
    Styling,
    ShippingAndReturns,
    ProductSkeleton,
    Aftercare,
    StickyAddToBag,
    FabricAndPrint,
    NostoProductRecommendations,
    CompleteTheSet,
    StockNotificationForm,
    HowToStyleIt,
    PointsBanner,
  },
  inject: ["mq"],
  props: {
    productHandle: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      loading: true,
      showStickyAddToBag: false,
      nostoProductRecommendationsProduct: null,
      nostoProductRecommendationsProductId: null,
      nostoProductRecommendationsBehaviour: null,
      nostoProductRecommendationsBehaviourId: null,
      referredPlacement: null,
      showStockNotification: false,
    };
  },
  computed: {
    ...mapState("auth", ["customer"]),
    ...mapState("product", ["product"]),
    ...mapState("add-to-bag", ["compactStockNotifOpen"]),
    ...mapGetters("auth", ["isLoggedIn"]),
    ...mapGetters("app", ["currencyCode"]),
    ...mapGetters("product", [
      "variants",
      "sellingPlanAllocation",
      "availableForPreOrder",
      "variant",
    ]),
    pointsBalance() {
      return parseFloat(this.customer?.approvedPoints?.value) || 0;
    },
    creditBalance() {
      if (!this.currencyCode) return;
      const value =
        this.pointsBalance / businessLogic.loyaltyPointsConversion || 0;

      return this.$filters.toCurrency(value, this.currencyCode, 0);
    },
    showPointsBanner() {
      const creditValue =
        this.pointsBalance / businessLogic.loyaltyPointsConversion || 0;
      const minimumValue = 0.99;

      const aboveMinimumValue = creditValue > minimumValue;

      return this.isLoggedIn && aboveMinimumValue;
    },
    isEDDEnabled() {
      if (this.currencyCode) {
        return regionFeatureToggles[this.currencyCode].eddEnabled || false;
      }
      return false;
    },
    showDeliveryEstimateComponent() {
      return this.isEDDEnabled;
    },
    productId() {
      return this.product?.id;
    },
    showVariants() {
      return this.variants.length > 1;
    },
    sellingPlanDescription() {
      return this.sellingPlanAllocation?.sellingPlan?.description;
    },
    showSellingPlanDescription() {
      if (this.availableForPreOrder && this.sellingPlanDescription) {
        return true;
      }

      return false;
    },
  },
  watch: {
    productId(val) {
      if (!val) {
        return;
      }

      const splitProductId = this.productId.split("Product/")[1];

      // eslint-disable-next-line no-undef
      nostojs((api) => {
        api
          .defaultSession()
          .viewProduct(val)
          .setRef(splitProductId, this.referredPlacement)
          .setPlacements(["productpage-nosto-1", "productpage-nosto-2"])
          .load()
          .then((data) => {
            this.nostoProductRecommendationsProduct =
              data.recommendations["productpage-nosto-1"]?.products || null;
            this.nostoProductRecommendationsProductId =
              data.recommendations["productpage-nosto-1"]?.result_id || null;
            this.nostoProductRecommendationsBehaviour =
              data.recommendations["productpage-nosto-2"]?.products || null;
            this.nostoProductRecommendationsBehaviourId =
              data.recommendations["productpage-nosto-2"]?.result_id || null;
          });
      });

      viewProductEvent(this.product);

      this.setRecentlyViewedProducts(this.product.id);
    },
  },
  created() {
    this.params = getParams(window.location);
    this.referredPlacement = this.params["ref-placement"];
  },
  methods: {
    ...mapActions("app", ["setRecentlyViewedProducts"]),
    ...mapActions("product", [
      "setProduct",
      "setSelectedVariant",
      "setDefaultVariant",
    ]),
    handleIntersection(entries) {
      if (this.mq.mdPlus) {
        return;
      }

      let addToBagVisible = false;
      let productDetailsVisible = false;

      entries.forEach((entry) => {
        if (entry.target === this.$refs.addToBag) {
          // Intersection Observer for addToBag
          if (entry.boundingClientRect.top < 0) {
            addToBagVisible = !entry.isIntersecting;
          }
        } else if (entry.target === this.$refs["product-details"]) {
          // Intersection Observer for product-details
          this.productDetailsIntersecting = entry.isIntersecting;
          productDetailsVisible = entry.isIntersecting;
        }
      });

      this.showStickyAddToBag = addToBagVisible || productDetailsVisible;
    },
    findPreSelectedVariant() {
      const params = getParams();
      const variantId = params.variant;
      if (variantId) {
        const variant = this.product.variants.edges.find(
          ({ node }) => node.id === "gid://shopify/ProductVariant/" + variantId
        );

        if (variant) {
          this.setSelectedVariant(variant.node);
        }
      }
    },
  },
  apollo: {
    product: {
      query: productQuery,
      skip() {
        return !this.productHandle;
      },
      variables() {
        return {
          product: this.productHandle,
        };
      },
      result(resp) {
        const product = resp?.data?.product;

        this.setProduct(product);
        this.findPreSelectedVariant();

        this.setDefaultVariant(product.variants.edges[0].node);

        if (product.variants.edges.length === 1) {
          this.setSelectedVariant(product.variants.edges[0].node);
        }

        this.loading = false;

        this.$nextTick(() => {
          const combinedObserver = new IntersectionObserver(
            this.handleIntersection
          );

          combinedObserver.observe(this.$refs.addToBag);
          combinedObserver.observe(this.$refs["product-details"]);
        });
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.product-grid {
  @include respond-above(lg) {
    display: grid;
    grid-template-columns: 50% 50%;
    padding: 40px 40px 60px 40px;
  }

  @include respond-above(xxl) {
    grid-template-columns: 60% 40%;
  }
}

.product {
  border-top: 1px solid $color-grey-light;

  &__media {
    position: relative;
  }

  &__title {
    @include editorial-headline(20px);
    margin: 0 0 8px 0;
    color: $color-black;

    @include respond-above(lg) {
      @include editorial-headline(30px);
      margin: 0;
    }
  }

  &__title-wrapper {
    margin-bottom: 28px;

    @include respond-above(lg) {
      margin-bottom: 40px;
    }
  }

  &__info {
    position: relative;

    .icon-text:not(:last-child) {
      margin: 0 0 8px 0;
    }
  }

  &__info-inner {
    padding: 20px 20px 60px 20px;

    @include respond-above(md) {
      padding: 20px 160px 60px 160px;
    }

    @include respond-above(lg) {
      padding: 0 0 0 30px;
    }
  }

  &__info-narrow {
    padding-right: 50px;
  }

  &__price {
    @include utility-body(14px);
    margin: 0;
  }

  &__bnpl {
    margin-top: 20px;
    padding: 20px 0;
    border-top: 1px solid $color-grey-light;
    border-bottom: 1px solid $color-grey-light;

    :deep(.provider, .text) {
      margin: 0;
      @include utility-body(15px);
      line-height: unset;
      color: $color-grey-dark;
    }
  }

  &__payment {
    margin-bottom: 40px;
  }

  &__accordion {
    margin-bottom: 20px;

    > .accordion:last-of-type {
      border-bottom: 1px solid $color-grey-light;
    }
  }
}

.variants-component {
  margin-bottom: 40px;
}

.grid {
  @include respond-above(lg) {
    display: flex;
    justify-content: space-between;
    gap: 10px;
  }

  .main {
    flex: 1 1 auto;
  }
}

.wishlist-button {
  position: absolute;
  top: 10px;
  right: 10px;

  @include respond-above(lg) {
    position: static;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: all 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translateY(100%);
}

:deep(.wysiwyg > p) {
  @include editorial-body(14px);
}

:deep(.sustainability > .text) {
  @include utility-body(15px);
}

:deep {
  .read-more {
    @include editorial-body(14px);

    .bullets {
      margin-left: 4px;
    }

    .bullets__bullet {
      @include editorial-body(14px);
    }
  }
}

.icon-text {
  :deep(.text) {
    @include utility-label(15px);
    text-transform: none;
  }

  :deep(.icon) {
    font-size: 20px;
    height: 20px;
    width: 20px;
  }
}

.sellingPlanDescription {
  @include utility-label(14px);
  margin-bottom: 5px;
}

// .points-banner {
//   background-color: #fafafa;
//   padding: 20px 0;
//   text-align: center;
//   @include utility-body(15px);
// }
</style>
